import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { formatDate, fullMonthAndYear, Table } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import {
    useCategoriesComparison,
    type ICategoryComparison,
} from "app/pages/industry-analysis/categories-context/CategoriesContext";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";
import { ConversionRateCardShell } from "../../shared/ConversionRateCardShell";
import { ConversionRateSectionHeader } from "../../shared/ConversionRateSectionHeader";
import { resolveCardState } from "../../shared/resolveCardState";
import { COMPARISON_AUTOMATION_IDS } from "../automationIds";
import { buildComparisonRows } from "../buildComparisonRows";

import { computeMaxConvertedVisits, getComparisonOverviewPopColumns } from "./getComparisonOverviewPopColumns";
import { useConversionRateComparisonOverviewPopData } from "./useConversionRateComparisonOverviewPopData";

interface IConversionRateComparisonOverviewPopProps {
    categories: ICategoryComparison[];
    availabilityState: "available" | "locked";
}

const LOADER_ROW_COUNT = 2;

export const ConversionRateComparisonOverviewPop = ({
    categories,
    availabilityState,
}: IConversionRateComparisonOverviewPopProps) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();
    const { hasSelectedCategoryWithSegments } = useCategoriesComparison();

    const country = params.country as number;
    const includeSubdomains = (params.includeSubdomains as boolean | undefined) ?? true;
    const durationParam = (params.duration as string) ?? BasicDurations.LAST_MONTH;
    const comparedDurationParam = params.comparedDuration as string | undefined;
    const {
        iso: { from, to, compareFrom, compareTo },
        isWindow,
    } = DurationService.getDurationData(durationParam, comparedDurationParam).forAPI;

    const hasCompareRange = Boolean(compareFrom && compareTo);

    const sharedParams = {
        country,
        includeSubdomains,
        from,
        to,
        compareFrom: compareFrom ?? "",
        compareTo: compareTo ?? "",
        isWindow,
    };

    const { data, isLoading } = useConversionRateComparisonOverviewPopData(categories, sharedParams, {
        enabled: availabilityState === "available" && hasCompareRange,
        hasSelectedCategoryWithSegments,
    });

    const rows = useMemo(() => buildComparisonRows(categories, data), [categories, data]);
    const maxConvertedVisits = useMemo(() => computeMaxConvertedVisits(rows), [rows]);
    const isYearOverYear = comparedDurationParam === BasicDurations.LAST_TWELVE_MONTHS;
    const columnConfig = useMemo(
        () =>
            getComparisonOverviewPopColumns(
                translate,
                COMPARISON_AUTOMATION_IDS.cardPop,
                isYearOverYear,
                maxConvertedVisits,
            ),
        [translate, isYearOverYear, maxConvertedVisits],
    );

    if (!hasCompareRange) {
        return null;
    }

    const countryText = CountryService.getCountryById(country)?.text ?? "";
    const dateRangeText = formatDate(from, to, fullMonthAndYear);
    const compareDateRangeText = formatDate(compareFrom, compareTo, fullMonthAndYear);
    const separatorText = translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonSeparator);
    const cardState = resolveCardState(availabilityState, isLoading);

    if (cardState === "available" && rows.every((row) => !row.entry)) {
        return null;
    }

    return (
        <Flex
            flexDirection="column"
            gap="16px"
            margin="8px 0 0 0"
            data-automation={COMPARISON_AUTOMATION_IDS.sectionPop}
        >
            <ConversionRateSectionHeader
                isComparison
                isPeriodOverPeriod
            />
            <ConversionRateCardShell
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonCardTitle)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonCardTooltip)}
                dateRangeText={dateRangeText}
                compareDateRangeText={compareDateRangeText}
                separatorText={separatorText}
                countryCode={country}
                countryText={countryText}
                state={cardState}
                cardAutomationId={COMPARISON_AUTOMATION_IDS.cardPop}
                lockedCtaAutomationId={COMPARISON_AUTOMATION_IDS.lockedCtaPop}
                loadingRowCount={LOADER_ROW_COUNT}
            >
                <Table
                    theme="white"
                    tableLayout="auto"
                    sticky={false}
                    data={rows}
                    columnConfig={columnConfig}
                    bordersConfig={{ body: false, header: false }}
                    rowKey={(row) => row.id}
                    data-automation={COMPARISON_AUTOMATION_IDS.tablePop}
                />
            </ConversionRateCardShell>
        </Flex>
    );
};
